import {
  AgDataGrid,
  AgDataGridProps,
  PaginationModel,
  domainCodeAtom,
} from '@australiangreens/ag-internal-components';
import { environment } from '@/config';
import { EXPANDED_TABLE_PROPERTIES } from '@/constants';
import { Supporter } from '@/types';
import { createBaseContactUrl, createRocketUrlForContact, createSortingString } from '@/util';
import { CollapsibleText } from '@components/CollapsibleText';
import { Link } from '@mui/material';
import Chip, { ChipProps } from '@mui/material/Chip';
import {
  DataGridProps,
  GridCallbackDetails,
  GridCellParams,
  GridColumnVisibilityModel,
  GridFeatureMode,
  GridFilterModel,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { CallingStatusValues } from '@/constants/callingStatus';
import { useAtomValue } from 'jotai';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { rocketColumn } from './columnDefintions';
import {
  assigneeNameFilterOperators,
  detailIdFilterOperators,
  getAssigneeDisplayName,
  notesFilterOperators,
  redisBetterOperators,
  redisNameFilterOperators,
  searchResultsPhoneFilterOperators,
} from './columnOperators';

const filteredSupportersListMessages = 'There are no supporters to display shown by the filter.';

export function phoneNoObscurer(phoneNo: string) {
  if (phoneNo) {
    const phoneFirst = phoneNo.slice(0, 2);
    const phoneLast = phoneNo.slice(-3);
    return `${phoneFirst}** *** ${phoneLast}`;
  }
  return '';
}

const statusColors: Record<(typeof CallingStatusValues)[number], ChipProps['color']> = {
  Uncalled: 'warning',
  Skipped: 'warning',
  'No answer': 'secondary',
  Answered: 'primary',
  'Meaningful interaction': 'primary',
};

function generateContactTableColumns(
  onClickHandler: (parameter: number) => void,
  idUrl?: (id: number) => string
): AgDataGridProps['columns'] {
  return [
    {
      headerName: 'Id',
      headerAlign: 'left',
      field: 'id',
      align: 'left',
      flex: 7,
      editable: false,
      filterOperators: detailIdFilterOperators,
      renderCell: (params: GridRenderCellParams) => {
        return idUrl ? (
          <Link href={idUrl(params.row.id)} target="_blank" title="Open Rocket in a new tab">
            {params.row.id}
          </Link>
        ) : (
          <span>{params.row.id}</span>
        );
      },
    },

    {
      headerName: 'First Name',
      field: 'preferredName',
      align: 'left',
      flex: 8,
      editable: false,
      valueGetter: (value, row) => {
        return createSortingString(row.preferredName, row.id);
      },
      renderCell: (params) => (
        <span
          role="presentation"
          onClick={() => {
            onClickHandler(params.row.id);
          }}
        >
          {params.row.preferredName}
        </span>
      ),
      filterOperators: redisNameFilterOperators,
    },
    {
      headerName: 'Last Name',
      field: 'lastName',
      align: 'left',
      flex: 8,
      editable: false,
      valueGetter: (value, row) => {
        return createSortingString(row.lastName, row.id);
      },
      renderCell: (params) => (
        <span
          role="presentation"
          onClick={() => {
            onClickHandler(params.row.id);
          }}
        >
          {params.row.lastName}
        </span>
      ),
      filterOperators: redisNameFilterOperators,
    },
    {
      headerName: 'Phone No.',
      field: 'phone',
      align: 'left',
      flex: 8,
      editable: false,
      valueGetter: (value, row) => {
        return createSortingString(row.phone === '' ? 'null' : row.phone, row.id);
      },
      renderCell: (params) => (
        <span
          role="presentation"
          onClick={() => {
            onClickHandler(params.row.id);
          }}
        >
          {phoneNoObscurer(params.row.phone)}
        </span>
      ),
      filterOperators: redisBetterOperators,
    },
    {
      headerName: 'Postcode',
      field: 'postalCode',
      align: 'left',
      flex: 6,
      editable: false,
      valueGetter: (value, row) => {
        return createSortingString(row.postalCode, row.id);
      },
      renderCell: (params) => (
        <span
          role="presentation"
          onClick={() => {
            onClickHandler(params.row.id);
          }}
        >
          {params.row.postalCode}
        </span>
      ),
      filterOperators: searchResultsPhoneFilterOperators,
    },
    {
      headerName: 'Assigned to',
      field: 'assigneeName',
      align: 'left',
      flex: 10,
      editable: false,
      valueGetter: (_value, row) => getAssigneeDisplayName(row.assigneeName),
      sortComparator: (v1, v2, param1, param2) => {
        const cmp = String(v1 ?? '').localeCompare(String(v2 ?? ''));
        if (cmp !== 0) {
          return cmp;
        }
        return Number(param1.id) - Number(param2.id);
      },
      renderCell: (params) => (
        <>
          {!params.row.assigneeName && (
            <span style={{ color: 'rgba(0,0,0,0.54)' }}>Unassigned</span>
          )}
          {params.row.assigneeName && (
            <>
              <Chip label={params.row.assigneeName} size="small" />
            </>
          )}
        </>
      ),
      filterOperators: assigneeNameFilterOperators,
    },
    {
      headerName: 'Calling status',
      field: 'callingStatus',
      align: 'left',
      flex: 12,
      type: 'singleSelect',
      valueOptions: CallingStatusValues,
      editable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.callingStatus}
          color={statusColors[params.row.callingStatus]}
          size="small"
          variant={
            ['Meaningful interaction', 'Uncalled'].includes(params.row.callingStatus)
              ? 'filled'
              : 'outlined'
          }
        />
      ),
    },
    rocketColumn(12),
    {
      headerName: 'Notes',
      field: 'latestNotes',
      align: 'left',
      valueGetter: (value, row) => {
        return createSortingString(row.latestNotes, row.id);
      },
      renderCell: (params) => <CollapsibleText text={params.row.latestNotes} />,
      flex: 36,
      editable: false,
      filterOperators: notesFilterOperators,
    },
  ];
}

type DetailedContactsTableProps = {
  data: Supporter[];
  isLoading: boolean;
  paginationModel: PaginationModel;
  onPaginationModelChange?: (model: PaginationModel) => void;
  onClickHandler: (parameter: number) => void;
  paginationMode?: DataGridProps['paginationMode'];
  filterMode?: GridFeatureMode;
  onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  supporterSelectionModel?: Supporter[];
  setSupporterSelectionModel?: Dispatch<SetStateAction<Supporter[]>>;
} & Partial<AgDataGridProps>;

const DetailedContactsTable = (props: DetailedContactsTableProps) => {
  const {
    data,
    isLoading,
    paginationModel,
    onPaginationModelChange = undefined,
    rowCount = undefined,
    sortModel = undefined,
    onSortModelChange = undefined,
    onClickHandler = (_x: number) => {},
    paginationMode,
    filterMode,
    onFilterModelChange,
    supporterSelectionModel,
    setSupporterSelectionModel,
  } = props;

  const domainCode = useAtomValue(domainCodeAtom);

  const isDev = ['test', 'local', 'dev'].includes(environment);
  const baseContactUrl = createBaseContactUrl(domainCode ?? 'ag', isDev);
  const contactIdUrl = useCallback(
    (id?: number) => createRocketUrlForContact(baseContactUrl, id),
    [baseContactUrl]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });

  return (
    <AgDataGrid
      rowSelectionModel={
        supporterSelectionModel === undefined
          ? undefined
          : { type: 'include' as const, ids: new Set(supporterSelectionModel.map((s) => s.id)) }
      }
      onRowSelectionModelChange={
        setSupporterSelectionModel
          ? (value: GridRowSelectionModel) => {
              const selectedIds = value.ids;
              const selectedRows = data.filter((row) => selectedIds.has(row.id));
              return setSupporterSelectionModel((oldModel) =>
                oldModel
                  .filter(
                    (oldContact) =>
                      selectedIds.has(oldContact.id) &&
                      !selectedRows.some((sr) => sr.id === oldContact.id)
                  )
                  .concat(selectedRows)
              );
            }
          : undefined
      }
      disableRowSelectionExcludeModel
      keepNonExistentRowsSelected
      paginationMode={paginationMode}
      sortingMode={paginationMode === 'server' ? 'server' : 'client'}
      filterMode={filterMode}
      onFilterModelChange={onFilterModelChange}
      loading={isLoading}
      rows={data}
      columns={generateContactTableColumns(onClickHandler, contactIdUrl)}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      noResultsMessage={filteredSupportersListMessages}
      skeletonColumnWidths={[11, 9, 8, 7, 11, 14, 12, 42]}
      rowCount={rowCount}
      checkboxSelection
      disableRowSelectionOnClick
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
      getCellClassName={(params: GridCellParams) => {
        if (params.field === 'latestNotes') {
          return 'latestNotes';
        }
        return '';
      }}
      {...EXPANDED_TABLE_PROPERTIES}
    />
  );
};

export default DetailedContactsTable;
